import { getRecordsByInterval } from "@/api/record";
import { parseError } from "@/api/errors";
import { RECORDS_PAGE_SIZE } from "@/common/constants";
import {
  HGIntersectionType,
  HGInterval,
  HGTimeRecordPlus,
} from "@/common/types";
import { getNow } from "@/main";
import {
  ExternalLoadResult,
  ExternalSource,
  ExternalSourceStatus,
  externalSourcesKey,
  loadExternalSource,
} from "@/providers/external/source";
import { HGStacks } from "@/stack/stacks";
import getGlobalStacks from "@/state/globalStacks";

/** Bursts of navigation collapse into one request after this quiet period. */
const LOAD_DEBOUNCE_MS = 120;
/**
 * How many previously loaded intervals to keep. Navigation is overwhelmingly
 * back-and-forth -- year into month and out again -- so even a small cache
 * turns most of it into no request at all.
 */
const CACHE_LIMIT = 12;

class DBRecordsReloader {
  #listeners: Set<() => void>;

  constructor() {
    this.#listeners = new Set<() => void>();
  }

  subscribe(callback: () => void) {
    this.#listeners.add(callback);
    return () => this.#listeners.delete(callback);
  }

  reload() {
    for (const callback of this.#listeners) {
      callback();
    }
  }
}

export const globalReloader = new DBRecordsReloader();

export interface DBRecordsSnapshot {
  records: HGTimeRecordPlus[];
  /** A response has come back for the current target. */
  loaded: boolean;
  /** A request is in flight; `records` may still be the previous interval's. */
  pending: boolean;
  /**
   * One entry per external source in scope. Carried in the snapshot rather
   * than thrown, because "Gmail needs reconnecting" is something the page has
   * to say next to the records that did load, not instead of them.
   */
  externalStatus: ExternalSourceStatus[];
  /**
   * Why the latest load failed, or null. Set only by a failure of the
   * database request (an external source failing is an `externalStatus`
   * line); `records` and `interval` are then still whatever was on screen
   * before, so the page can say "showing earlier results" rather than
   * "nothing here". Cleared by the next load that succeeds.
   */
  error: string | null;
  /**
   * The interval `records` were loaded for -- not necessarily the one the
   * stacks are on right now. The two diverge while a request is in flight, and
   * the tree has to be built against *this* one: pairing the new interval with
   * the old records would bucket every row into the wrong place.
   */
  interval: HGInterval | null;
  endInterval: HGInterval | null;
  /** Name of the interval above, for the tree's root label. */
  name: string;
}

export const EMPTY_SNAPSHOT: DBRecordsSnapshot = {
  records: [],
  loaded: false,
  pending: false,
  externalStatus: [],
  error: null,
  interval: null,
  endInterval: null,
  name: "",
};

interface LoadTarget {
  key: string;
  interval: HGInterval;
  endInterval: HGInterval | null;
  secondsRange: [bigint, bigint];
  name: string;
}

interface LoadedPage {
  records: HGTimeRecordPlus[];
  externalStatus: ExternalSourceStatus[];
}

export class DBRecordsLoader {
  #stacks: HGStacks;
  #userID: string | undefined;
  #collectionID: string | undefined;
  // Replaced rather than mutated so useSyncExternalStore can compare it.
  #snapshot: DBRecordsSnapshot = EMPTY_SNAPSHOT;
  #listeners: Set<() => void> = new Set<() => void>();
  /** The key being loaded, or waiting out the debounce. */
  #wantedKey: string | null = null;
  #cache: Map<string, LoadedPage> = new Map();
  /**
   * The provider collections in scope. Pushed in by `Records`, which already
   * holds the collections list, rather than read from Redux or RTK Query here:
   * this class deliberately depends on neither.
   */
  #externalSources: ExternalSource[] = [];
  #externalKey: string = "";
  #abort: AbortController | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  // Incremented whenever an in-flight request stops being the one we want.
  // The abort usually gets there first; this covers the window between the
  // response landing and the promise callback running.
  #generation: number = 0;
  #unsubscribes: (() => void)[] = [];

  constructor() {
    this.#stacks = getGlobalStacks();
    this.#unsubscribes.push(
      this.#stacks.subscribe(() => {
        this.#onStacksChange();
      }),
    );

    this.#unsubscribes.push(
      globalReloader.subscribe(() => {
        this.reset();
      }),
    );
  }

  setTarget(userID: string, collectionID?: string) {
    if (this.#userID === userID && this.#collectionID === collectionID) {
      return;
    }
    this.#userID = userID;
    this.#collectionID = collectionID;
    // A different user or collection is a different dataset, so nothing
    // already loaded is worth showing or keeping.
    this.#cache.clear();
    this.#snapshot = EMPTY_SNAPSHOT;
    this.#notify();
    this.#load(true);
  }

  /**
   * Which external systems to merge in alongside the database records.
   *
   * The key they hash to is part of the load key, so editing a Gmail query
   * invalidates the cached intervals it produced instead of showing them again.
   */
  setExternalSources(sources: ExternalSource[]) {
    const key = externalSourcesKey(sources);
    if (key === this.#externalKey) return;
    this.#externalKey = key;
    this.#externalSources = sources;
    this.#cache.clear();
    if (this.#userID) this.#load(true);
  }

  /** Drop everything cached and reload the current position. */
  reset() {
    this.#cache.clear();
    if (this.#userID) this.#load(true);
  }

  /// Stop receiving updates and invalidate any in-flight request.
  destroy() {
    for (const unsubscribe of this.#unsubscribes) {
      unsubscribe();
    }
    this.#unsubscribes = [];
    this.#cancel();
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
    this.#cache.clear();
    this.#listeners.clear();
  }

  getSnapshot(): DBRecordsSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #target(): LoadTarget | null {
    if (!this.#userID) return null;
    const interval = this.#stacks.getInterval();
    const endInterval = this.#stacks.getEndInterval();
    const end = endInterval ? `${endInterval.it}:${endInterval.n}` : "";
    return {
      key: `${this.#userID}|${this.#collectionID ?? ""}|${interval.it}:${interval.n}|${end}|${this.#externalKey}`,
      interval,
      endInterval,
      secondsRange: this.#stacks.getSecondsRange(),
      name: this.#stacks.getName(),
    };
  }

  #onStacksChange() {
    const target = this.#target();
    // Not every notification moves the cursor: switching focus between the
    // start and end chip changes no interval at all, and used to cost a full
    // refetch.
    if (!target || target.key === this.#wantedKey) return;
    this.#load(false);
  }

  #cancel() {
    this.#generation += 1;
    this.#abort?.abort();
    this.#abort = null;
  }

  #load(immediate: boolean) {
    const target = this.#target();
    if (!target) return;
    this.#wantedKey = target.key;

    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;

    // A cached interval is published straight away -- debouncing it would only
    // add latency to the one case that has none.
    const cached = this.#cache.get(target.key);
    if (cached) {
      this.#cancel();
      this.#publish(target, cached);
      return;
    }

    if (immediate) {
      this.#start(target);
    } else {
      this.#timer = setTimeout(() => {
        this.#timer = null;
        const current = this.#target();
        // The cursor may have moved on during the wait; reload() aside, only
        // ever fetch what is wanted now.
        if (current && current.key === this.#wantedKey) this.#start(current);
      }, LOAD_DEBOUNCE_MS);
    }
  }

  #start(target: LoadTarget) {
    this.#cancel();
    const generation = this.#generation;
    const controller = new AbortController();
    this.#abort = controller;

    // The previous interval's records stay on screen behind `pending` rather
    // than being wiped here: a navigation that resolves quickly then has no
    // visible transition at all, and the timeline keeps its DOM.
    this.#snapshot = { ...this.#snapshot, pending: true };
    this.#notify();

    this.#fetchAll(target, controller.signal)
      .then((page) => {
        if (generation !== this.#generation) return;
        const filtered = {
          records: this.#filterRecords(page.records, target),
          externalStatus: page.externalStatus,
        };
        this.#remember(target.key, filtered);
        this.#publish(target, filtered);
      })
      .catch((err) => {
        if (generation !== this.#generation) return;
        const error = parseError(err, "The records could not be loaded.");
        if (error.status === "aborted") return;
        console.error(err);
        // Leave whatever is on screen alone, but stop claiming to be busy.
        this.#snapshot = {
          ...this.#snapshot,
          loaded: true,
          pending: false,
          error: error.message,
        };
        this.#notify();
      });
  }

  /**
   * The database and every external system in scope, in parallel.
   *
   * `allSettled`, deliberately: an expired Gmail grant must not blank the
   * records that did load beside it. A failed source contributes no rows and
   * one status line, and the timeline is otherwise unaffected.
   */
  async #fetchAll(
    target: LoadTarget,
    signal: AbortSignal,
  ): Promise<LoadedPage> {
    const userID = this.#userID;
    if (!userID) return { records: [], externalStatus: [] };

    const dbRequest = getRecordsByInterval(
      target.interval,
      target.endInterval,
      target.secondsRange,
      RECORDS_PAGE_SIZE,
      userID,
      this.#collectionID,
      signal,
    );

    const externalRequests = this.#externalSources.map((source) =>
      loadExternalSource(
        source,
        { secondsRange: target.secondsRange, userId: userID },
        signal,
      ),
    );

    const [db, ...external] = await Promise.allSettled([
      dbRequest,
      ...externalRequests,
    ]);

    // The database failing is still a failure of the whole load -- there is no
    // useful page without it, and the catch upstream keeps the previous one on
    // screen rather than showing a half-empty timeline.
    if (db.status === "rejected") throw db.reason;

    const records = [...(db.value as HGTimeRecordPlus[])];
    const externalStatus: ExternalSourceStatus[] = [];

    for (let i = 0; i < external.length; i++) {
      const settled = external[i] as PromiseSettledResult<ExternalLoadResult>;
      if (settled.status === "fulfilled") {
        records.push(...settled.value.records);
        externalStatus.push(settled.value.status);
        continue;
      }
      if (
        settled.reason instanceof DOMException &&
        settled.reason.name === "AbortError"
      ) {
        continue;
      }
      const source = this.#externalSources[i];
      externalStatus.push({
        collectionId: source.collectionId,
        providerKey: source.providerKey,
        title: source.providerKey,
        state: "error",
        message:
          settled.reason instanceof Error
            ? settled.reason.message
            : "Could not be read.",
      });
    }

    return { records, externalStatus };
  }

  #publish(target: LoadTarget, page: LoadedPage) {
    this.#snapshot = {
      records: page.records,
      loaded: true,
      pending: false,
      externalStatus: page.externalStatus,
      error: null,
      interval: target.interval,
      endInterval: target.endInterval,
      name: target.name,
    };
    this.#notify();
  }

  #remember(key: string, page: LoadedPage) {
    // Re-inserting moves the key to the end, so the first key is always the
    // least recently used one.
    this.#cache.delete(key);
    this.#cache.set(key, page);
    while (this.#cache.size > CACHE_LIMIT) {
      const oldest = this.#cache.keys().next().value;
      if (oldest === undefined) break;
      this.#cache.delete(oldest);
    }
  }

  #notify() {
    for (const listener of this.#listeners) {
      listener();
    }
  }

  /**
   * An unfinished record from 2010 merely transits "next week" and has no
   * business being listed there.
   */
  #filterRecords(records: HGTimeRecordPlus[], target: LoadTarget) {
    if (target.secondsRange[0] <= getNow()["s"]) return records;
    return records.filter(
      (record) =>
        record.end_interval ||
        record.intersection.intersectionType !== HGIntersectionType.Transit,
    );
  }
}
